import { useState } from "react";
import { Download, Sparkles } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Cost, PageHeader, Pills, Section, Spinner } from "../components/AiHtml";
import { useGenerate } from "../lib/useGenerate";
import { api, handleErr } from "../lib/api";

const Research = ({ r }) => (
  <div className="space-y-6" data-testid="research-result">
    <Section title={r.title}><p className="text-sm text-slate-300 leading-relaxed">{r.summary}</p></Section>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {(r.stats || []).map((s, i) => <div key={i} className="card-n p-4"><p className="text-2xl font-display font-bold text-orange-400">{s.value}</p><p className="text-xs text-slate-400 mt-1">{s.label}</p></div>)}
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      {(r.sections || []).map((s, i) => (
        <Section key={i} title={s.heading}><ul className="space-y-2 text-sm text-slate-300">{s.points.map((p, j) => <li key={j} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />{p}</li>)}</ul></Section>
      ))}
    </div>
    {r.competitors?.length > 0 && (
      <Section title="Competitors">
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-slate-500 text-xs"><th className="py-2">Name</th><th>Strength</th><th>Weakness</th></tr></thead>
          <tbody>{r.competitors.map((c, i) => <tr key={i} className="border-t border-slate-800 align-top"><td className="py-2 pr-3 font-medium">{c.name}</td><td className="pr-3 text-emerald-300/90">{c.strength}</td><td className="text-red-300/90">{c.weakness}</td></tr>)}</tbody></table></div>
      </Section>
    )}
    {r.recommendations?.length > 0 && <Section title="Recommendations"><ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-300 marker:text-orange-400">{r.recommendations.map((x, i) => <li key={i}>{x}</li>)}</ol></Section>}
  </div>
);

const Slide = ({ s, i }) => (
  <div data-testid={`ppt-slide-${i}`} className="aspect-video rounded-xl bg-[#0B1120] border border-slate-800 p-5 flex flex-col relative overflow-hidden">
    <div className="absolute top-5 left-0 w-1.5 h-10 bg-orange-500" />
    <p className="font-mono text-[10px] text-slate-500 mb-1">SLIDE {i + 1}</p>
    <p className={`font-display font-bold text-slate-50 leading-tight ${i === 0 ? "text-xl" : "text-base"}`}>{s.title}</p>
    {s.subtitle && <p className="text-xs text-orange-400 mt-1 line-clamp-2">{s.subtitle}</p>}
    <ul className="mt-3 space-y-1 text-[11px] text-slate-300 overflow-hidden">{(s.bullets || []).map((b, j) => <li key={j} className="line-clamp-2">• {b}</li>)}</ul>
  </div>
);

export default function ResearchStudio() {
  const [mode, setMode] = useState("research");
  const [prompt, setPrompt] = useState("");
  const g = useGenerate(mode);
  const [dl, setDl] = useState(false);

  const download = async () => {
    setDl(true);
    try {
      const res = await api.post("/ppt/download", g.result, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${g.result.title || "deck"}.pptx`;
      a.click();
    } catch (e) { handleErr(e); } finally { setDl(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader label="Research & PPT Studio" title="Market research and pitch decks" desc="Structured research rendered as clean cards, and 6-slide decks you can download as PPTX." />
      <div className="card-n p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Pills testPrefix="research-mode" value={mode} onChange={(m) => { setMode(m); g.setResult(null); }} options={[{ value: "research", label: "Market Research" }, { value: "ppt", label: "Pitch Deck (6 slides)" }]} />
          <Cost n={mode === "ppt" ? 15 : 10} />
        </div>
        <Textarea data-testid="research-prompt-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Organic skincare D2C market in India for women 22-35" />
        <Button data-testid="research-generate-btn" disabled={g.busy || !prompt.trim()} onClick={() => g.run(prompt, {}, mode)} className="rounded-full"><Sparkles size={16} className="mr-1.5" /> Generate</Button>
      </div>
      {g.busy && <div className="card-n"><Spinner label={mode === "ppt" ? "Designing your 6 slides..." : "Researching the market..."} /></div>}
      {!g.busy && g.result && mode === "research" && <Research r={g.result} />}
      {!g.busy && g.result && mode === "ppt" && (
        <Section title={g.result.title} right={<Button data-testid="ppt-download-btn" size="sm" onClick={download} disabled={dl} className="rounded-full"><Download size={14} className="mr-1" /> {dl ? "Building..." : "Download PPTX"}</Button>}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{(g.result.slides || []).slice(0, 6).map((s, i) => <Slide key={i} s={s} i={i} />)}</div>
        </Section>
      )}
    </div>
  );
}
